/**
 * Locale resolution utilities for speech dictation.
 *
 * Converts between backend locale codes (underscore format, e.g. "en_US")
 * and BCP-47 tags expected by speech recognition APIs (hyphen format,
 * e.g. "en-US"). Resolves the dictation language by preferring the user's
 * stored preference, falling back to the device locale, and defaulting
 * to en-US.
 *
 * The list of supported locales is fetched from the backend via the
 * `supportedLocales` query — no hardcoded enum duplication needed.
 * @module utils/locale
 */

import { getLocales } from "expo-localization";

import { toBcp47 } from "@/utils/intlFormat";

/**
 * Maps a two-letter language subtag to a human-readable display name.
 * Used to derive labels from backend locale codes without hardcoding
 * the full enum on the frontend.
 */
const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  pt: "Portuguese",
  it: "Italian",
  de: "German",
  nl: "Dutch",
  ja: "Japanese",
  zh: "Chinese",
  ko: "Korean",
  ar: "Arabic",
  ru: "Russian",
  pl: "Polish",
  tr: "Turkish",
  sv: "Swedish",
  da: "Danish",
  no: "Norwegian",
};

/**
 * Maps region codes to display names for disambiguation when multiple
 * locales share the same language (e.g. pt_BR vs pt_PT).
 */
const REGION_DISPLAY_NAMES: Record<string, string> = {
  BR: "Brazil",
  PT: "Portugal",
  US: "US",
  GB: "UK",
  MX: "Mexico",
  ES: "Spain",
  CA: "Canada",
  SA: "Saudi Arabia",
  CN: "Simplified",
  TW: "Traditional",
  SE: "Sweden",
  DK: "Denmark",
  NO: "Norway",
  AT: "Austria",
};

/**
 * Returns a display label for a locale code.
 * When the full locale has a known region, includes it in parentheses
 * for disambiguation (e.g. "pt_BR" → "Portuguese (Brazil)").
 * Single-region languages omit the region (e.g. "de_DE" → "German").
 * @param localeCode - Backend locale code (e.g. "en_US", "pt_BR")
 * @param allLocales - Full list of supported locales, used to detect
 *   when disambiguation is needed. When omitted, always includes region.
 * @returns Human-readable language name
 */
function getLocaleDisplayName(
  localeCode: string,
  allLocales?: readonly string[]
): string {
  const parts = localeCode.split("_");
  const subtag = parts[0]?.toLowerCase() ?? "";
  const region = parts[1]?.toUpperCase() ?? "";
  const baseName = LANGUAGE_DISPLAY_NAMES[subtag] ?? localeCode;

  if (!region) return baseName;

  const regionLabel = REGION_DISPLAY_NAMES[region];
  if (!regionLabel) return baseName;

  const needsDisambiguation = allLocales
    ? allLocales.filter(l => l.split("_")[0]?.toLowerCase() === subtag).length >
      1
    : true;

  return needsDisambiguation ? `${baseName} (${regionLabel})` : baseName;
}

/**
 * Converts a backend locale code to a BCP-47 language tag.
 *
 * SE-7966: delegates to {@link toBcp47} rather than carrying its own
 * `replaceAll`. Two independent copies of this conversion is how the two
 * drift, and the failure mode when they do is silent — an unconverted
 * `en_US` does not throw, it just formats against the wrong locale. This
 * one feeds speech-recognition APIs rather than `Intl`, so it is a
 * different CONSUMER of the same single conversion, not a second rule.
 * @param locale - Backend locale code (e.g. "en_US")
 * @returns BCP-47 tag (e.g. "en-US")
 */
function localeToBCP47(locale: string): string {
  return toBcp47(locale);
}

/**
 * Finds the closest supported locale for a BCP-47 device language tag.
 * Prefers an exact region match (e.g. "pt-PT" → "pt_PT") before falling
 * back to language-only matching (e.g. "pt-PT" → "pt_BR" if no pt_PT).
 * @param bcp47 - BCP-47 tag from device (e.g. "es-MX", "en", "fr-FR")
 * @param supportedLocales - List of backend-supported locale codes
 * @returns Matching locale code or null if no match
 */
function findClosestLocale(
  bcp47: string,
  supportedLocales: readonly string[]
): string | null {
  const asUnderscore = bcp47.replaceAll("-", "_");
  const exactMatch = supportedLocales.find(
    locale => locale.toLowerCase() === asUnderscore.toLowerCase()
  );
  if (exactMatch) return exactMatch;

  const subtag = bcp47.split("-")[0]?.toLowerCase() ?? "";
  return (
    supportedLocales.find(
      locale => locale.split("_")[0]?.toLowerCase() === subtag
    ) ?? null
  );
}

/** Default fallback locale */
const DEFAULT_LOCALE = "en_US";

/**
 * Detects the device's primary locale and maps it to the closest
 * backend-supported locale. Falls back to en_US if unsupported.
 * @param supportedLocales - List of backend-supported locale codes
 * @returns The closest supported locale for the device language
 */
function getDeviceLocale(supportedLocales: readonly string[]): string {
  const locales = getLocales();
  const deviceTag = locales[0]?.languageTag ?? "en-US";
  return findClosestLocale(deviceTag, supportedLocales) ?? DEFAULT_LOCALE;
}

/**
 * Resolves the BCP-47 language tag to use for speech dictation.
 * Priority: user preference → device locale → en-US.
 * @param userPreference - The user's stored languagePreference, if any
 * @param supportedLocales - List of backend-supported locale codes
 * @returns BCP-47 language tag for speech recognition APIs
 */
function resolveDictationLocale(
  userPreference: string | null | undefined,
  supportedLocales: readonly string[]
): string {
  const locale = userPreference ?? getDeviceLocale(supportedLocales);
  return localeToBCP47(locale);
}

export {
  DEFAULT_LOCALE,
  findClosestLocale,
  getDeviceLocale,
  getLocaleDisplayName,
  localeToBCP47,
  resolveDictationLocale,
};
